import { useRef, useState } from 'react';
import {
  Upload,
  Loader2,
  FileText,
  Check,
  AlertTriangle,
  Download,
  RefreshCw,
  ExternalLink,
  ScanLine,
  Unlink,
  Undo2,
} from 'lucide-react';
import Button from '../components/Button.jsx';
import { formatCurrency, formatDate } from '../lib/format.js';
import {
  useUploadReceiptBatch,
  useReceiptBatch,
  useConfirmReceiptItem,
  useUnmatchReceiptItem,
  useCancelReceiptUpload,
  useUploadAllMatched,
  receiptPreviewUrl,
  unmatchedZipUrl,
} from '../hooks/useReceiptBatches.js';

const STATUS_BADGE = {
  matched: { label: 'Rapproché', cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
  ambiguous: { label: 'À choisir', cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  unmatched: { label: 'Non rapproché', cls: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200' },
  uploaded: { label: 'Uploadé', cls: 'bg-sky-50 text-sky-700 ring-1 ring-sky-200' },
  pending: { label: 'En attente', cls: 'bg-slate-50 text-slate-500 ring-1 ring-slate-200' },
  error: { label: 'Erreur', cls: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200' },
};

function money(cents) {
  return cents == null ? '—' : formatCurrency(Math.abs(cents));
}

// TVA : détail par taux si multi-taux (ex. resto 10 % + 20 %), sinon taux unique + montant.
function vatText(ex) {
  const bd = ex?.vat_breakdown ?? [];
  if (bd.length > 1) {
    const parts = bd.map((b) => `${b.rate ?? '?'}% ${money(b.vat_amount_cents)}`).join(' · ');
    return `TVA : ${parts} (tot. ${money(ex.vat_amount_cents)})`;
  }
  if (ex?.vat_rate != null) {
    return `TVA ${ex.vat_rate}%${ex.vat_amount_cents != null ? ` (${money(ex.vat_amount_cents)})` : ''}`;
  }
  if (ex?.vat_amount_cents != null) return `TVA ${money(ex.vat_amount_cents)}`;
  return null;
}

function CandidateOption({ c, taken }) {
  const who = c.counterparty_name || c.label || 'Transaction';
  // Priorité des alertes : prise dans CE lot > rapprochée par un lot précédent > pièce déjà sur Qonto.
  const flag = taken
    ? ` · ⚠ ${taken.status === 'uploaded' ? 'uploadé' : 'pris par'} p.${taken.pageNo + 1}`
    : c.already_claimed
      ? ` · ⚠ ${c.claimed_status === 'uploaded' ? 'déjà uploadé' : 'déjà rapproché'}`
      : c.has_qonto_attachments
        ? ' · 📎 pièce jointe Qonto'
        : '';
  return `${who} · ${money(c.amount_cents)} · ${formatDate(c.operation_date)}${c.entity_name ? ` · ${c.entity_name}` : ''} (${c.score}%)${flag}`;
}

function ItemCard({ batchId, item, takenBy }) {
  const confirm = useConfirmReceiptItem(batchId);
  const unmatch = useUnmatchReceiptItem(batchId);
  const cancelUpload = useCancelReceiptUpload(batchId);
  const candidates = item.candidates ?? [];
  // Transaction prise par UNE AUTRE page de ce lot (rapprochée ou uploadée) — à jour en
  // direct après chaque confirm/unmatch/cancel via l'invalidation react-query.
  const takenByOther = (c) => {
    const t = takenBy?.get(c.transaction_id);
    return t && t.itemId !== item.id ? t : null;
  };
  // Avec l'attribution globale, le match retenu n'est plus forcément le 1er candidat
  // (les transactions déjà réclamées restent listées devant lui le cas échéant).
  const matchedIdx = candidates.findIndex((c) => c.transaction_id === item.match_transaction_id);
  const [selected, setSelected] = useState(
    item.status === 'matched' ? Math.max(0, matchedIdx) : ''
  );
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const ex = item.extracted ?? {};
  const badge = STATUS_BADGE[item.status] ?? STATUS_BADGE.pending;
  const selectedCandidate = selected === '' ? null : candidates[Number(selected)];
  const mutationError = confirm.error ?? unmatch.error ?? cancelUpload.error;

  function handleConfirm() {
    const idx = selected === '' ? null : Number(selected);
    if (idx == null || !candidates[idx]) return;
    const c = candidates[idx];
    confirm.mutate({ itemId: item.id, transaction_id: c.transaction_id, entity_id: c.entity_id });
  }

  return (
    <li className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-400">Page {item.page_no + 1}</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
        </div>
        <a
          href={receiptPreviewUrl(batchId, item.id)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
        >
          <FileText size={13} /> Aperçu <ExternalLink size={11} />
        </a>
      </div>

      {/* Champs extraits */}
      <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
        <span className="font-medium text-slate-800">{ex.label || ex.merchant || '—'}</span>
        <span className="tabular-nums text-slate-700">{money(ex.amount_cents)}</span>
        <span className="text-slate-500">{ex.date ? formatDate(ex.date) : '—'}</span>
        {vatText(ex) && <span className="text-xs text-slate-400">{vatText(ex)}</span>}
      </div>

      {/* Rapprochement */}
      <div className="mt-3">
        {item.status === 'uploaded' && (
          <div className="flex flex-wrap items-center gap-2">
            <p className="inline-flex items-center gap-1.5 text-sm text-sky-700">
              <Check size={14} /> Justificatif uploadé sur la transaction
            </p>
            {!confirmingCancel && (
              <Button
                variant="ghost"
                onClick={() => setConfirmingCancel(true)}
                className="shrink-0 px-2 py-1 text-xs text-slate-500"
              >
                <Undo2 size={13} /> Annuler l'upload
              </Button>
            )}
            {confirmingCancel && (
              <span className="inline-flex flex-wrap items-center gap-2 text-xs text-slate-600">
                Retirer le justificatif de Qonto ?
                <Button
                  variant="ghost"
                  disabled={cancelUpload.isPending}
                  onClick={() =>
                    cancelUpload.mutate(item.id, { onSettled: () => setConfirmingCancel(false) })
                  }
                  className="px-2 py-1 text-xs text-rose-600"
                >
                  {cancelUpload.isPending ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    'Oui, annuler'
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setConfirmingCancel(false)}
                  className="px-2 py-1 text-xs"
                >
                  Non
                </Button>
              </span>
            )}
          </div>
        )}

        {item.status === 'unmatched' && (
          <p className="inline-flex items-center gap-1.5 text-sm text-slate-500">
            <AlertTriangle size={14} /> Aucun rapprochement — inclus dans le ZIP
          </p>
        )}

        {item.status === 'error' && (
          <p className="text-sm text-rose-600">{item.error || 'Erreur de traitement'}</p>
        )}

        {(item.status === 'matched' || item.status === 'ambiguous') && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-700"
              >
                {item.status === 'ambiguous' && <option value="">Choisir une transaction…</option>}
                {candidates.map((c, i) => (
                  <option key={c.transaction_id} value={i}>
                    {CandidateOption({ c, taken: takenByOther(c) })}
                  </option>
                ))}
              </select>
              <Button
                onClick={handleConfirm}
                disabled={selected === '' || confirm.isPending}
                className="shrink-0"
              >
                {confirm.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Confirmer'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => unmatch.mutate(item.id)}
                disabled={unmatch.isPending || confirm.isPending}
                className="shrink-0 px-2 py-1 text-xs text-slate-500"
              >
                {unmatch.isPending ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <><Unlink size={13} /> Ne pas rapprocher</>
                )}
              </Button>
            </div>
            {selectedCandidate && takenByOther(selectedCandidate) && (
              <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 ring-1 ring-amber-200">
                <AlertTriangle size={12} className="-mt-0.5 mr-1 inline" />
                {takenByOther(selectedCandidate).status === 'uploaded'
                  ? `Justificatif déjà uploadé sur cette transaction par la page ${takenByOther(selectedCandidate).pageNo + 1} de ce lot.`
                  : `Déjà rapprochée avec la page ${takenByOther(selectedCandidate).pageNo + 1} de ce lot.`}
                <a
                  href={receiptPreviewUrl(batchId, takenByOther(selectedCandidate).itemId)}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-1 font-medium underline hover:text-amber-900"
                >
                  Voir ce ticket
                </a>
              </div>
            )}
            {selectedCandidate?.already_claimed && !takenByOther(selectedCandidate) && (
              <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 ring-1 ring-amber-200">
                <AlertTriangle size={12} className="-mt-0.5 mr-1 inline" />
                {selectedCandidate.claimed_status === 'uploaded'
                  ? 'Un justificatif a déjà été uploadé sur cette transaction (lot précédent).'
                  : 'Cette transaction est déjà rapprochée dans un lot précédent.'}
                {selectedCandidate.claimed_preview_available && (
                  <a
                    href={receiptPreviewUrl(
                      selectedCandidate.claimed_batch_id,
                      selectedCandidate.claimed_item_id
                    )}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-1 font-medium underline hover:text-amber-900"
                  >
                    Voir le ticket existant
                  </a>
                )}
              </div>
            )}
          </>
        )}
        {mutationError && (
          <p className="mt-1 text-xs text-rose-600">{mutationError.message ?? 'Échec de l’opération'}</p>
        )}
      </div>
    </li>
  );
}

function BatchReview({ batchId, onReset }) {
  const { data, isLoading, isError } = useReceiptBatch(batchId);
  const uploadAll = useUploadAllMatched(batchId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Chargement…
      </div>
    );
  }
  if (isError || !data) {
    return <p className="text-rose-600">Impossible de charger le lot.</p>;
  }

  const { batch, items } = data;
  const counts = items.reduce((acc, it) => {
    acc[it.status] = (acc[it.status] ?? 0) + 1;
    return acc;
  }, {});
  // Quelle page du lot a pris quelle transaction — 'uploaded' prime sur 'matched'.
  const takenBy = new Map();
  for (const it of items) {
    if ((it.status === 'matched' || it.status === 'uploaded') && it.match_transaction_id) {
      const prev = takenBy.get(it.match_transaction_id);
      if (!prev || (prev.status !== 'uploaded' && it.status === 'uploaded')) {
        takenBy.set(it.match_transaction_id, { itemId: it.id, pageNo: it.page_no, status: it.status });
      }
    }
  }
  const processing = batch.status === 'processing';

  return (
    <div className="space-y-4">
      {/* Progression / résumé */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-800">{batch.filename}</p>
            <p className="text-xs text-slate-500">
              {processing
                ? `Analyse en cours — ${batch.processed_pages}/${batch.total_pages} pages`
                : `${batch.total_pages} page${batch.total_pages > 1 ? 's' : ''} analysées`}
            </p>
          </div>
          <Button variant="ghost" onClick={onReset} className="shrink-0">
            <RefreshCw size={14} /> Nouveau lot
          </Button>
        </div>

        {processing && (
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-slate-800 transition-all"
              style={{ width: `${Math.round((100 * batch.processed_pages) / Math.max(1, batch.total_pages))}%` }}
            />
          </div>
        )}

        {!processing && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              onClick={() => uploadAll.mutate()}
              disabled={!counts.matched || uploadAll.isPending}
            >
              {uploadAll.isPending ? (
                <><Loader2 size={14} className="animate-spin" /> Envoi…</>
              ) : (
                <><Check size={14} /> Uploader les matchs sûrs ({counts.matched ?? 0})</>
              )}
            </Button>
            <a
              href={unmatchedZipUrl(batchId)}
              className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm ${
                counts.unmatched ? 'text-slate-700 hover:bg-slate-50' : 'pointer-events-none text-slate-300'
              }`}
            >
              <Download size={14} /> ZIP non-rapprochés ({counts.unmatched ?? 0})
            </a>
            {uploadAll.isSuccess && (
              <span className="text-xs text-emerald-600">
                {uploadAll.data?.uploaded ?? 0} uploadé(s){uploadAll.data?.failed ? `, ${uploadAll.data.failed} échec(s)` : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Liste des pages */}
      <ul className="space-y-2">
        {items.map((it) => (
          <ItemCard key={it.id} batchId={batchId} item={it} takenBy={takenBy} />
        ))}
      </ul>
    </div>
  );
}

export default function ReceiptImportPage() {
  const [batchId, setBatchId] = useState(null);
  const inputRef = useRef(null);
  const upload = useUploadReceiptBatch();

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const res = await upload.mutateAsync(file).catch(() => null);
    if (res?.batch_id) setBatchId(res.batch_id);
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 space-y-5">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
          <ScanLine size={20} /> Import justificatifs
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Déposez un scan PDF multi-pages (un ticket par page). Chaque page est analysée, rapprochée
          d'une transaction (Juvo / Legendary), puis — après revue — le justificatif est envoyé sur
          Qonto. Les tickets non rapprochés se téléchargent en ZIP.
        </p>
      </header>

      {!batchId && (
        <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white p-8 text-center">
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={handleFile}
          />
          <Upload size={28} className="mx-auto text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">PDF multi-pages · 100 Mo max</p>
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            className="mt-4"
          >
            {upload.isPending ? (
              <><Loader2 size={14} className="animate-spin" /> Envoi…</>
            ) : (
              <><Upload size={14} /> Choisir un PDF</>
            )}
          </Button>
          {upload.isError && (
            <p className="mt-2 text-xs text-rose-600">{upload.error?.message ?? "Échec de l'envoi"}</p>
          )}
        </div>
      )}

      {batchId && <BatchReview batchId={batchId} onReset={() => setBatchId(null)} />}
    </div>
  );
}
